import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient, queryOptions } from '@tanstack/react-query';
import { toast } from 'sonner';
import { queryKeys } from '@/api/queryKeys';
import { queryClient } from '@/api/queryClient';
import { DocumentType, SubstanceVerificationStatus } from '@/api/generated-types';
import useWorkflowStore from '@/stores/workflowStore';
import { globalValidationStore } from '@/stores/validationStore';
import {
  convertValidationResults,
  hasUsableVerificationResults,
} from '@/utils/validation/validationUtils';
import { commitRehydrated, type CommitPorts } from '@/utils/validation/commit';
import { validateDocumentFields } from '@/utils/validation/documentValidation';
import { verifyAllSubstances } from '@/services/substances/api';
import {
  getExportPreview,
  getTransactionFull,
  getTransactionPdf,
  getTransactionArtifactText,
  getPageImage,
  getErrorReview,
  exportDocument,
  saveCuratedData,
  fetchInitialErrorReport,
  fetchPostValidationErrorReport,
} from './api';
import type {
  CompatibilityCheckResponse,
  HazcheckValidationRequest,
  CuratedArtifactResponse,
  CargoSectionResult,
  ErrorReport,
  FormDangerousGoodsItem,
  SaveValidationResponse,
  SubstanceVerificationResponse,
  SubstanceVerificationResult,
  TransactionResponse,
} from '@/api/types';
import type { ValidationFormData } from '@/types/validationFormTypes';

export const transactionFullQueryOptions = (transactionId: string, enabled: boolean = true) =>
  queryOptions({
    queryKey: queryKeys.transactions.full(transactionId),
    queryFn: () => getTransactionFull(transactionId),
    enabled: enabled && !!transactionId,
    // Fresh until a write removes or invalidates it.
    staleTime: Infinity,
  });

export const exportPreviewQueryOptions = (transactionId: string, enabled: boolean = true) =>
  queryOptions({
    queryKey: queryKeys.transactions.exportPreview(transactionId),
    queryFn: () => getExportPreview(transactionId),
    enabled: enabled && !!transactionId,
    staleTime: Infinity,
  });

export const errorReviewQueryOptions = (transactionId: string, enabled: boolean = true) =>
  queryOptions({
    queryKey: queryKeys.transactions.errorReview(transactionId),
    queryFn: () => getErrorReview(transactionId),
    enabled: enabled && !!transactionId,
  });

export const initialErrorReportQueryOptions = (transactionId: string) =>
  queryOptions({
    queryKey: queryKeys.transactions.initialErrorReport(transactionId),
    queryFn: async () => (await fetchInitialErrorReport(transactionId)) ?? null,
    staleTime: 0,
  });

export const transactionPdfQueryOptions = (transactionId: string, enabled: boolean = true) =>
  queryOptions({
    queryKey: queryKeys.transactions.pdf(transactionId),
    queryFn: () => getTransactionPdf(transactionId),
    enabled: enabled && !!transactionId,
    // Multi-MB Blob, immutable per transactionId, so a background refetch must not re-download it.
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
  });

export const artifactTextQueryOptions = (transactionId: string, artifactName: string) =>
  queryOptions({
    queryKey: queryKeys.transactions.artifact(transactionId, artifactName),
    queryFn: () => getTransactionArtifactText(transactionId, artifactName),
    staleTime: Infinity,
  });

// Hydrate orchestrator: see CONTEXT.md > Hydration source
interface HydrateTransactionParams {
  transaction: TransactionResponse;
  artifacts: {
    curated: CuratedArtifactResponse | undefined;
    formData: ValidationFormData | undefined;
    originalMappedGoods: FormDangerousGoodsItem[];
    layoutDetection: CargoSectionResult | undefined;
    validationResponse: CompatibilityCheckResponse | undefined;
    validationRequest: HazcheckValidationRequest | undefined;
    substanceValidation: SubstanceVerificationResponse | undefined;
    savedValidation: SaveValidationResponse | undefined;
  };
  hasCurated: boolean;
}

type HydrationPlan =
  | { kind: 'curated'; response: SubstanceVerificationResponse | undefined }
  | { kind: 'serverValidated'; response: SubstanceVerificationResponse }
  | { kind: 'freshAuto' };

function planHydration(params: HydrateTransactionParams): HydrationPlan {
  const sub = params.artifacts.substanceValidation;

  if (params.hasCurated && params.artifacts.curated) {
    return { kind: 'curated', response: sub };
  }
  if (hasUsableVerificationResults(sub?.results)) {
    return { kind: 'serverValidated', response: sub! };
  }
  return { kind: 'freshAuto' };
}

const ports: CommitPorts = { validation: globalValidationStore, workflow: useWorkflowStore };

function restoreFromCurated(
  plan: Extract<HydrationPlan, { kind: 'curated' }>,
  formData: ValidationFormData
): void {
  // /full ships curated results merged into substanceValidation, never in curated itself.
  const results = plan.response?.results;
  if (!results?.length) return;

  const converted = convertValidationResults(
    results,
    plan.response?.enrichedSubstances,
    formData.dangerousGoods ?? []
  );
  globalValidationStore.getState().batchCommit(converted);
}

function restoreFromServer(
  plan: Extract<HydrationPlan, { kind: 'serverValidated' }>,
  formData: ValidationFormData
): void {
  commitRehydrated(ports, plan.response, formData);
}

async function autoValidate(formData: ValidationFormData): Promise<void> {
  const { metadata, localTransactionId } = useWorkflowStore.getState();
  if (!localTransactionId) return;

  try {
    const response = await verifyAllSubstances({
      transactionId: localTransactionId,
      regulationSources: metadata.regulationSources,
      dangerousGoods: formData.dangerousGoods ?? [],
      cargoTransportUnits: formData.cargoTransportUnits,
      documentType: formData.documentType ?? DocumentType.Dgd,
    });
    if (!response) return;
    commitRehydrated(ports, response, formData);
  } catch (err) {
    console.error('[hydrate/freshAuto]', err);
    toast.error(
      'Automatic substance validation failed. You can validate manually using the Validate button.'
    );
  }
}

/**
 * Queued, not run here: the retry resolves after the form has mounted and taken ownership of
 * the values, so its commit has to reach RHF and the error check to be worth anything.
 */
function queueRetry(results: SubstanceVerificationResult[]): void {
  const failed = results
    .filter((r) => r.validationStatus === SubstanceVerificationStatus.Error && r.cargoId)
    .map((r) => r.cargoId!);
  if (failed.length > 0) useWorkflowStore.getState().setPendingSubstanceRetry(failed);
}

function fetchErrorReportForPlan(
  plan: HydrationPlan,
  transactionId: string | undefined,
  formData: ValidationFormData
): Promise<ErrorReport | null | undefined> {
  if (plan.kind === 'curated') {
    return transactionId
      ? queryClient.fetchQuery(initialErrorReportQueryOptions(transactionId))
      : Promise.resolve(undefined);
  }
  return fetchPostValidationErrorReport(transactionId, formData);
}

async function hydrateTransaction(params: HydrateTransactionParams): Promise<void> {
  globalValidationStore.getState().resetValidation();
  useWorkflowStore.getState().hydrateFromArtifacts({
    transaction: params.transaction,
    curated: params.artifacts.curated,
    formData: params.artifacts.formData,
    originalMappedGoods: params.artifacts.originalMappedGoods,
    layoutDetection: params.artifacts.layoutDetection?.layoutDetection,
    cargoBbox: params.artifacts.layoutDetection?.cargoBbox,
    validationResponse: params.artifacts.validationResponse,
    validationRequest: params.artifacts.validationRequest,
    savedValidation: params.artifacts.savedValidation,
  });

  const formData = useWorkflowStore.getState().formData;
  globalValidationStore.getState().setExtractionWarnings(validateDocumentFields(formData));

  if (params.artifacts.substanceValidation?.comparisons) {
    globalValidationStore
      .getState()
      .setComparisons(params.artifacts.substanceValidation.comparisons);
  }

  const plan = planHydration(params);
  switch (plan.kind) {
    case 'curated':
      restoreFromCurated(plan, formData);
      break;
    case 'serverValidated':
      restoreFromServer(plan, formData);
      break;
    case 'freshAuto':
      await autoValidate(formData);
      break;
  }
  queueRetry(globalValidationStore.getState().validationResults);

  const errorReport = await fetchErrorReportForPlan(
    plan,
    params.transaction.id,
    useWorkflowStore.getState().formData
  );

  if (errorReport)
    globalValidationStore.getState().applyErrorReport(errorReport, { mergeWithExisting: true });
}

// Save mutations call `markHydrationStale()` to force a re-fetch next visit.
export async function ensureTransactionHydrated(transactionId: string): Promise<void> {
  if (useWorkflowStore.getState().hydratedTransactionId === transactionId) return;

  void queryClient.prefetchQuery(transactionPdfQueryOptions(transactionId));
  const full = await queryClient.fetchQuery(transactionFullQueryOptions(transactionId));
  await hydrateTransaction({
    transaction: full.transaction ?? { id: transactionId, userId: '', filename: '' },
    artifacts: {
      curated: full.curated ?? undefined,
      formData: full.formData ?? undefined,
      originalMappedGoods: full.originalMappedGoods ?? [],
      layoutDetection: full.layoutDetection ?? undefined,
      validationResponse: full.validationResponse ?? undefined,
      validationRequest: full.validationRequest ?? undefined,
      substanceValidation: full.substanceValidation ?? undefined,
      savedValidation: full.savedValidation ?? undefined,
    },
    hasCurated: !!full.curated?.form_data,
  });
}

export function usePageImageQuery(imageUrl: string | null) {
  return useQuery({
    queryKey: queryKeys.transactions.pageImage(imageUrl ?? ''),
    queryFn: () => getPageImage(imageUrl!),
    enabled: !!imageUrl,
    staleTime: Infinity,
    gcTime: 2 * 60 * 1000,
  });
}

type CuratedWriteVars = { transactionId: string; curatedData: CuratedArtifactResponse };

function useCuratedWrite<TResult>(mutationFn: (vars: CuratedWriteVars) => Promise<TResult>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: (_: TResult, { transactionId }: CuratedWriteVars) => {
      // Remove, not invalidate: invalidating refetches the open page's /full straight away,
      // which is large on a big DCM. The next loader run fetches it fresh.
      queryClient.removeQueries({ queryKey: queryKeys.transactions.full(transactionId) });
      queryClient.invalidateQueries({
        queryKey: queryKeys.transactions.exportPreview(transactionId),
      });
      useWorkflowStore.getState().markHydrationStale();
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.curation.all });
    },
  });
}

export function useSaveCuratedData() {
  return useCuratedWrite(({ transactionId, curatedData }) =>
    saveCuratedData(transactionId, curatedData)
  );
}

export function useExportDocument() {
  return useCuratedWrite(({ transactionId, curatedData }) =>
    exportDocument(transactionId, curatedData)
  );
}

export function useOpenBlobInTab() {
  const mutation = useMutation({
    mutationFn: ({ transactionId }: { transactionId: string; tab: Window }) =>
      queryClient.fetchQuery(transactionPdfQueryOptions(transactionId)),
    onSuccess: (blob, { tab }) => {
      const url = URL.createObjectURL(blob);
      tab.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    },
    onError: (_, { tab }) => tab.close(),
  });

  // The tab opens inside the click; opened after the download, popup blockers treat it as unsolicited.
  const openTransactionPdf = (transactionId: string) => {
    const tab = window.open('', '_blank');
    if (!tab) return;
    tab.opener = null;
    mutation.mutate({ transactionId, tab });
  };

  return { openTransactionPdf, isLoading: mutation.isPending };
}

export function useTransactionPrefetch() {
  const queryClient = useQueryClient();

  return useMemo(
    () => ({
      prefetchArtifacts: (transactionId: string) =>
        queryClient.prefetchQuery(transactionFullQueryOptions(transactionId)),
      prefetchPdf: (transactionId: string) =>
        queryClient.prefetchQuery(transactionPdfQueryOptions(transactionId)),
    }),
    [queryClient]
  );
}
